import React, { useEffect, useMemo, useState } from "react"
import { ChevronLeft, ChevronRight, Shirt } from "lucide-react"
import { api, json } from "../api"
import { Link, useNavigate, useParams } from "../router"
import { Badge, Button, Card, ErrorNotice, PageHeader } from "../ui"

const positions = ["P", "C", "1B", "2B", "3B", "SS", "LF", "CF", "LC", "RC", "RF", "Bench"]

export function PlayerPage() {
  const { id } = useParams("/players/:id")
  const navigate = useNavigate()
  const [player, setPlayer] = useState(null)
  const [abilities, setAbilities] = useState({ batting: 3, fielding: 3 })
  const [error, setError] = useState(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    api(`/players/${id}`)
      .then(data => {
        setPlayer(data)
        setAbilities({ batting: data.batting_ability, fielding: data.fielding_ability })
      })
      .catch(setError)
  }, [id])

  const gameHistory = useMemo(() => {
    if (!player) return []
    const rows = new Map()
    player.game_lineups.forEach(entry => {
      const row = rows.get(entry.game_id) || { game: entry.game, positions: [], batting: null }
      row.positions.push(`${entry.inning}: ${entry.field_position}`)
      rows.set(entry.game_id, row)
    })
    player.game_batting_orders.forEach(entry => {
      const row = rows.get(entry.game_id) || { game: entry.game, positions: [], batting: null }
      row.batting = entry.batting_order
      rows.set(entry.game_id, row)
    })
    return [...rows.values()].sort((a, b) => new Date(b.game.game_date) - new Date(a.game.game_date))
  }, [player])

  async function save(event) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const values = Object.fromEntries(form)
    values.batting_ability = Number(values.batting_ability)
    values.fielding_ability = Number(values.fielding_ability)
    values.can_pitch = form.has("can_pitch")
    values.can_catch = form.has("can_catch")
    values.preferred_positions = form.getAll("preferred_positions")
    try {
      const updated = await api(`/players/${id}`, json("PATCH", { player: values }))
      setPlayer(current => ({ ...current, ...updated }))
      setSaved(true)
    } catch (caught) { setError(caught) }
  }

  async function remove() {
    if (!window.confirm(`Remove ${player.first_name} from the roster?`)) return
    try { await api(`/players/${id}`, { method: "DELETE" }); navigate(`/teams/${player.team_id}`) } catch (caught) { setError(caught) }
  }

  if (!player) return <div className="page"><p>Loading player…</p><ErrorNotice error={error} /></div>

  const practiceHistory = player.practice_attendances || []
  const preferredPositions = player.preferred_positions || []
  const headerDetails = [
    player.jersey_number != null ? `Jersey #${player.jersey_number}` : "No jersey assigned",
    player.can_pitch ? "Pitcher" : null,
    player.can_catch ? "Catcher" : null,
  ].filter(Boolean).join(" · ")

  return <div className="page player-page">
    <Link className="back-link" to={`/teams/${player.team_id}`}><ChevronLeft size={17} /> Back to {player.team.name}</Link>
    <PageHeader eyebrow={`${player.team.name} · ${player.team.season}`} title={`${player.first_name} ${player.last_name || ""}`} copy={headerDetails} actions={saved ? <Badge tone="green">Changes saved</Badge> : null} />
    <ErrorNotice error={error} />

    <div className="player-profile-grid">
      <Card className="panel player-editor">
        <div className="profile-panel-header">
          <div>
            <p className="eyebrow">Roster profile</p>
            <h2>Player information</h2>
            <p>Keep lineup details and family contacts current.</p>
          </div>
        </div>

        <form className="player-form" onSubmit={save} onChange={() => setSaved(false)}>
          <section className="profile-form-section">
            <div className="profile-form-section__heading">
              <h3>Basics</h3>
              <p>The name and number shown throughout the team workspace.</p>
            </div>
            <div className="form-grid form-grid--3">
              <label>First name<input name="first_name" defaultValue={player.first_name} required /></label>
              <label>Last name<input name="last_name" defaultValue={player.last_name || ""} /></label>
              <label>Jersey number<input name="jersey_number" type="number" min="0" defaultValue={player.jersey_number ?? ""} /></label>
            </div>
          </section>

          <section className="profile-form-section">
            <div className="profile-form-section__heading">
              <h3>Lineup profile</h3>
              <p>Use these ratings and roles when planning fair lineups.</p>
            </div>
            <div className="ability-grid">
              <label className="ability-control">
                <span className="ability-control__header"><span>Batting ability</span><Badge tone="blue">{abilities.batting} of 5</Badge></span>
                <input name="batting_ability" type="range" min="1" max="5" value={abilities.batting} onChange={event => setAbilities(current => ({ ...current, batting: Number(event.target.value) }))} />
                <span className="ability-scale"><span>Developing</span><span>Advanced</span></span>
              </label>
              <label className="ability-control">
                <span className="ability-control__header"><span>Fielding ability</span><Badge tone="blue">{abilities.fielding} of 5</Badge></span>
                <input name="fielding_ability" type="range" min="1" max="5" value={abilities.fielding} onChange={event => setAbilities(current => ({ ...current, fielding: Number(event.target.value) }))} />
                <span className="ability-scale"><span>Developing</span><span>Advanced</span></span>
              </label>
            </div>

            <fieldset className="profile-choice-fieldset">
              <legend className="sr-only">Player roles</legend>
              <div className="role-choice-grid">
                <label className="role-choice"><input name="can_pitch" type="checkbox" defaultChecked={player.can_pitch} /><span><strong>Pitcher</strong><small>Eligible for pitching assignments.</small></span></label>
                <label className="role-choice"><input name="can_catch" type="checkbox" defaultChecked={player.can_catch} /><span><strong>Catcher</strong><small>Eligible for catching assignments.</small></span></label>
              </div>
            </fieldset>

            <fieldset className="profile-choice-fieldset">
              <legend>Preferred positions</legend>
              <div className="profile-position-grid">
                {positions.map(position => <label className="position-choice" key={position}><input name="preferred_positions" value={position} type="checkbox" defaultChecked={preferredPositions.includes(position)} /><span>{position}</span></label>)}
              </div>
            </fieldset>
          </section>

          <section className="profile-form-section">
            <div className="profile-form-section__heading">
              <h3>Family contacts</h3>
              <p>Add the people coaches should contact about availability and team updates.</p>
            </div>
            <div className="contact-group-grid">
              <div className="contact-group">
                <h4>Primary contact</h4>
                <label>Name<input name="parent_name_1" defaultValue={player.parent_name_1 || ""} /></label>
                <label>Phone<input name="parent_phone_1" type="tel" defaultValue={player.parent_phone_1 || ""} /></label>
              </div>
              <div className="contact-group">
                <h4>Additional contact</h4>
                <label>Name<input name="parent_name_2" defaultValue={player.parent_name_2 || ""} /></label>
                <label>Phone<input name="parent_phone_2" type="tel" defaultValue={player.parent_phone_2 || ""} /></label>
              </div>
            </div>
          </section>

          <section className="profile-form-section">
            <div className="profile-form-section__heading">
              <h3>Coach notes</h3>
              <p>Keep private context that helps with development and lineup planning.</p>
            </div>
            <label>Notes<textarea name="notes" defaultValue={player.notes || ""} rows="4" /></label>
          </section>

          <div className="profile-form-actions">
            <div className="profile-remove-action">
              <Button type="button" variant="danger" onClick={remove}>Remove player</Button>
              <p>Removes this player and their saved team history.</p>
            </div>
            <Button type="submit">Save player</Button>
          </div>
        </form>
      </Card>

      <Card className="panel player-activity-panel">
        <div className="activity-panel-header">
          <div>
            <p className="eyebrow">Player activity</p>
            <h2>History</h2>
          </div>
        </div>
        <dl className="activity-summary">
          <div><dt>Games</dt><dd>{gameHistory.length}</dd></div>
          <div><dt>Practices</dt><dd>{practiceHistory.length}</dd></div>
        </dl>

        {!gameHistory.length && !practiceHistory.length ? <div className="activity-empty"><Shirt size={16} /><div><h3>No activity yet</h3><p>Saved lineup assignments and attendance will appear here.</p></div></div> : null}

        {gameHistory.length ? <section className="activity-section">
          <div className="activity-section__header"><h3>Games</h3><Badge>{gameHistory.length}</Badge></div>
          <div className="activity-list">
            {gameHistory.map(row => <Link className="player-activity-row" key={row.game.id} to={`/games/${row.game.id}`}><div><p className="player-activity-row__title">vs {row.game.opponent}</p><p>{formatDate(row.game.game_date)} · Batting {row.batting || "—"}</p><p>{row.positions.join(" · ") || "No fielding assignment"}</p></div><ChevronRight size={16} /></Link>)}
          </div>
        </section> : null}

        {practiceHistory.length ? <section className="activity-section">
          <div className="activity-section__header"><h3>Practices</h3><Badge>{practiceHistory.length}</Badge></div>
          <div className="activity-list">
            {practiceHistory.map(entry => <Link className="player-activity-row" key={entry.id} to={`/practices/${entry.practice_id}`}><div><p className="player-activity-row__title">{formatDate(entry.practice.practice_date)}</p><p>{entry.practice.location || "No location"}</p></div><span className="activity-row-status"><Badge tone={entry.status === "present" ? "green" : "orange"}>{entry.status}</Badge><ChevronRight size={16} /></span></Link>)}
          </div>
        </section> : null}
      </Card>
    </div>
  </div>
}

function formatDate(value) {
  return new Date(value).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })
}
